import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { 
    Search, 
    Layers, 
    Rocket, 
    LineChart, 
    Maximize,
    ChevronRight
} from 'lucide-react';

const containerVariants = {
    hidden: {},
    visible: {
        transition: {
            staggerChildren: 0.18,
            delayChildren: 0.1
        }
    }
};

const cardVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: { 
        opacity: 1, 
        y: 0, 
        scale: 1,
        transition: { 
            type: "spring", 
            stiffness: 80, 
            damping: 14 
        } 
    }
};

const OurApproach = () => {
    const steps = [
        {
            num: "01",
            title: "DISCOVER",
            desc: "Understanding workforce gaps, operational bottlenecks, hiring inefficiencies, and growth goals.",
            icon: <Search size={24} />,
            color: "#38bdf8"
        },
        {
            num: "02",
            title: "DESIGN",
            desc: "Building customized workforce strategies aligned with recruitment and operational requirements.",
            icon: <Layers size={24} />,
            color: "#818cf8"
        },
        {
            num: "03",
            title: "DEPLOY",
            desc: "Executing staffing operations, recruitment workflows, and workforce infrastructure efficiently.",
            icon: <Rocket size={24} />,
            color: "#2dd4bf"
        },
        {
            num: "04",
            title: "OPTIMIZE",
            desc: "Continuously improving recruitment performance, payroll accuracy, and HR coordination.",
            icon: <LineChart size={24} />,
            color: "#fbbf24"
        },
        {
            num: "05",
            title: "SCALE",
            desc: "Supporting long-term expansion through scalable workforce systems and strategic frameworks.",
            icon: <Maximize size={24} />,
            color: "#f87171"
        }
    ];

    return (
        <SectionContainer>
            <div className="container mx-auto px-6 relative z-10">
                
                {/* --- HEADER --- */}
                <div className="max-w-4xl mb-24">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                      
                        <h2 className="text-3xl md:text-5xl font-bold syne text-slate-900 leading-tight mb-8">
                            Our Strategic <br /> <span className="text-blue-600">Approach.</span>
                        </h2>
                        <p className="text-slate-700 text-lg md:text-lg max-w-2xl leading-relaxed">
                            High-performing workforce systems are not built through reactive recruitment. They are built through structured strategy and scalable execution.
                        </p>
                    </motion.div>
                </div>

                {/* --- STEPS INTERACTIVE FLOW --- */}
                <motion.div 
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.15 }}
                    className="relative flex flex-col lg:flex-row gap-8 lg:gap-4 items-stretch"
                >
                    
                    {/* Horizontal Connector Line (Desktop Only) */}
                    <div className="absolute top-[45px] left-0 w-full h-[2px] hidden lg:block pointer-events-none z-0">
                        <svg className="w-full h-full" overflow="visible">
                            <motion.line 
                                x1="0%" y1="50%" x2="100%" y2="50%"
                                stroke="rgba(1, 1, 76, 0.1)" 
                                strokeWidth="2" 
                                strokeDasharray="6 4"
                                initial={{ pathLength: 0 }}
                                whileInView={{ pathLength: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 1.5, ease: "easeInOut", delay: 0.2 }}
                            />
                        </svg>
                    </div>

                    {steps.map((step, index) => (
                        <motion.div 
                            key={index}
                            variants={cardVariants}
                            className="relative flex-1 group"
                        >
                            {/* Step Number & Connector Node */}
                            <div className="flex lg:flex-col items-center gap-6 lg:gap-10 mb-8">
                                <div className="relative z-10">
                                    <motion.div 
                                        initial={{ scale: 0.7 }}
                                        whileInView={{ scale: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ type: "spring", stiffness: 120, delay: index * 0.15 + 0.3 }}
                                        className="w-16 h-16 rounded-3xl bg-white border border-slate-200 flex items-center justify-center transition-all duration-500 group-hover:border-blue-500 group-hover:-translate-y-2 group-hover:shadow-xl"
                                    >
                                        <span className="text-2xl font-bold syne text-slate-600 group-hover:text-slate-900 transition-colors">
                                            {step.num}
                                        </span>
                                    </motion.div>
                                    {/* Animated Progress Dot */}
                                    <motion.div 
                                        animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                                        transition={{ repeat: Infinity, duration: 3, delay: index }}
                                        className="absolute -top-1 -right-1 w-3 h-3 rounded-full shadow-lg"
                                        style={{ backgroundColor: step.color, boxShadow: `0 0 10px ${step.color}` }}
                                    />
                                </div>
                                <div className="h-[1px] flex-1 bg-slate-200 lg:hidden" />
                                <motion.div 
                                    initial={{ scale: 0, rotate: -45 }}
                                    whileInView={{ scale: 1, rotate: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ type: "spring", stiffness: 120, delay: index * 0.15 + 0.4 }}
                                    className="hidden lg:flex w-10 h-10 rounded-full items-center justify-center bg-slate-50 border border-slate-200 text-slate-600 group-hover:bg-[#01014C] group-hover:text-white transition-all group-hover:rotate-[360deg] duration-500"
                                >
                                    {step.icon}
                                </motion.div>
                            </div>

                            {/* Content Card */}
                            <div className="lg:pr-4">
                                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3 tracking-tighter syne">
                                    {step.title}
                                    <ChevronRight size={16} className="text-blue-600 opacity-0 group-hover:opacity-100 transition-all transform group-hover:translate-x-1" />
                                </h3>
                                <p className="text-slate-900 text-sm leading-relaxed group-hover:text-slate-900 transition-colors">
                                    {step.desc}
                                </p>
                            </div>

                            {/* Glowing Hover Bar */}
                            <div 
                                className="absolute -bottom-4 left-0 h-[2px] bg-gradient-to-r from-transparent via-blue-500 to-transparent transition-all duration-700 w-0 group-hover:w-full"
                                style={{ background: `linear-gradient(90deg, transparent, ${step.color}, transparent)` }}
                            />
                        </motion.div>
                    ))}
                </motion.div>

            </div>
        </SectionContainer>
        
    );
};

const SectionContainer = styled.section`
    background-color: #ffffff;
    padding: 100px 0;
    overflow: hidden;
    position: relative;

    &::before {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        background: radial-gradient(circle at 10% 20%, rgba(56, 189, 248, 0.05) 0%, transparent 50%);
        pointer-events: none;
    }
`;

export default OurApproach;